import axios from 'axios';
import { createBaseUrl } from './utils/create-base-url';
import { passThroughData } from './utils/data-formatter';
import { ThrivaApiAuthError } from './utils/ThrivaApiAuthError';
import { ThrivaApiRequestError } from './utils/ThrivaApiRequestError';
import { ThrivaApiServerError } from './utils/ThrivaApiServerError';
export const NODEJS_DEFAULT_REFERER_HEADER = `@thrivadev/thriva-js`;
/**
 * Default settings for API requests.
 */
let THRIVA_DEFAULT_API_REQUEST_OPTIONS = {
    apiVersion: 'v1',
    baseURL: '',
    method: 'GET',
    headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json'
    },
    withCredentials: true
};
export let THRIVA_DEFAULT_API_V2_REQUEST_OPTIONS = {
    apiVersion: 'v2',
    baseURL: '',
    method: 'GET',
    headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json'
    },
    withCredentials: true
};
async function makeRequest(axiosOptions, dataFormatter = passThroughData) {
    try {
        const response = await axios.request(axiosOptions);
        return dataFormatter(response);
    }
    catch (error) {
        return Promise.reject(checkForErrors(error, axiosOptions));
    }
}
/**
 * Check the error to see if we can use a Thriva error and provide more detail.
 *
 * @param error - The error that was caught.
 * @param options - The request options used for the request.
 * @returns A new error object based on ThrivaApiRequestError
 */
export function checkForErrors(error, options) {
    if (error.response) {
        const { data, status, statusText } = error.response;
        if (status >= 400 && status <= 499) {
            return new ThrivaApiAuthError(statusText, options.url || '', options, status, data);
        }
        if (status >= 500) {
            return new ThrivaApiServerError(statusText, options.url || '', options, status, data);
        }
    }
    if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        const { status, statusText } = error.request;
        return new ThrivaApiRequestError(statusText, options.url || '', options, status, error.request);
    }
    return new ThrivaApiRequestError(error.message, options.url || '', options, 0, error);
}
/**
 * Allows you to set the default request options for all requests.
 * @param options - The options object to use.
 */
export function setDefaultRequestOptions(apiOptionsV1, apiOptionsV2) {
    THRIVA_DEFAULT_API_REQUEST_OPTIONS = apiOptionsV1;
    THRIVA_DEFAULT_API_V2_REQUEST_OPTIONS = apiOptionsV2;
}
/**
 * Generic method for making HTTP requests to Thriva API endpoints.
 * @typeParam ItemType - The return type of the data.
 * @param url - The URL of the Thriva API endpoint.
 * @param requestOptions - Options for the request, including parameters relevant to the endpoint.
 * @returns A Promise that will resolve with the data from the response.
 */
export async function request(url, requestOptions, dataFormatter) {
    const axiosOptions = {
        ...THRIVA_DEFAULT_API_REQUEST_OPTIONS,
        ...requestOptions,
        ...{
            headers: {
                ...THRIVA_DEFAULT_API_REQUEST_OPTIONS.headers,
                ...requestOptions.headers
            },
            ...(requestOptions.params && { params: requestOptions.params })
        },
        url
    };
    if (axiosOptions.apiVersion) {
        axiosOptions.baseURL = createBaseUrl(axiosOptions.baseURL, axiosOptions.apiVersion);
    }
    // Make sure a referer is set if running in node
    if (typeof window === 'undefined' && !axiosOptions.headers.referer) {
        axiosOptions.headers.referer = NODEJS_DEFAULT_REFERER_HEADER;
    }
    return makeRequest(axiosOptions, dataFormatter);
}
//# sourceMappingURL=request.js.map